import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import {
  Application,
  Applicant,
  Medication,
  IssueDetail,
  Condition,
} from 'src/services/interfaces/application';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import { MONTH_MAP } from '@constants/index';
import { parseISO, isValid, addMonths, startOfMonth, format } from 'date-fns';
import { getCoverageTagForApplicantIndex } from './coverageLevel';

const sanitizeCategory = (category: string) =>
  category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

// Helper to get month name from number (e.g., 02 -> February)
const getMonthName = (monthNumber: string) => {
  const monthIndex = parseInt(monthNumber, 10) - 1;
  return (
    Object.keys(MONTH_MAP).find(
      (key) =>
        MONTH_MAP[key as keyof typeof MONTH_MAP] ===
        (monthIndex + 1).toString().padStart(2, '0')
    ) || ''
  );
};

// Helper to get first day of next month
const getFirstDayOfNextMonth = (): string => {
  const now = parseISO(format(new Date(), 'yyyy-MM-dd'));
  const nextMonth = addMonths(now, 1);
  const firstDay = startOfMonth(nextMonth);
  return format(firstDay, 'yyyy-MM-dd');
};

// Helper to validate and fix effective date if it's in the past
const validateEffectiveDate = (effectiveDate: string | null | undefined): string | null => {
  if (!effectiveDate) {
    return null;
  }

  try {
    const parsedDate = parseISO(effectiveDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only

    // If the date is in the past or invalid, return null
    if (!isValid(parsedDate) || parsedDate < today) {
      return null;
    }

    return effectiveDate;
  } catch {
    return null;
  }
};

export function transformApplicationToOnboardingData(
  application: Application,
  allPlans: InsurancePlan[]
): Partial<OnboardingSchema> {
  if (!application) return {};

  const transformedData: Partial<OnboardingSchema> = {};

  // 1. Transform Applicants
  if (application.applicants && application.applicants.length > 0) {
    // Set householdIncome at root level for primary applicant
    const primaryApplicant = application.applicants.find(
      (app) => app.applicant === 1
    );
    if (primaryApplicant) {
      transformedData.householdIncome = application.income;
      if (primaryApplicant.phone) {
        transformedData.phone = primaryApplicant.phone;
      }
    }

    transformedData.applicants = application.applicants.map(
      (app: Applicant) => {
        const [year, month, day] = app.dob.split('-');
        return {
          applicant:
            app.applicant === 1
              ? 'primary'
              : app.applicant === 2
                ? 'spouse'
                : 'dependent',
          firstName: app.firstName,
          lastName: app.lastName,
          midName: app.midName || '',
          gender: app.gender || 'male', // Default to 'male' to satisfy schema
          dateOfBirth: {
            year,
            month: getMonthName(month),
            day,
          },
          height: {
            ft: app.heightFt ?? 0, // Default to 0
            in: app.heightIn ?? 0, // Default to 0
          },
          weight: app.weight ?? 0, // Default to 0
          nicotine: app.nicotine,
          ssn: app.ssn || '',
        };
      }
    );
  }

  // 2. Transform Plans
  if (application.plans && allPlans.length > 0) {
    application.plans.forEach((plan) => {
      const fullPlan = allPlans.find((p: InsurancePlan) => p.id === plan.id);
      if (fullPlan && fullPlan.category) {
        const planFieldName =
          fullPlan.category === 'Major Medical'
            ? 'medicalPlan'
            : `${sanitizeCategory(fullPlan.category)}Plan`;
        (transformedData as unknown as Record<string, unknown>)[planFieldName] =
          fullPlan.uid;

        // Set tier for the plan
        if (plan.tier) {
          const tierFieldName = planFieldName.replace('Plan', 'PlanTier');
          (transformedData as unknown as Record<string, unknown>)[
            tierFieldName
          ] = plan.tier;
        }

        // Set coverage amount for the plan (used in Critical Illness)
        const coverageAmountFieldName = planFieldName.replace(
          'Plan',
          'PlanCoverageAmount'
        );
        let coverageAmount: number | undefined;

        if (plan.coverageAmount) {
          coverageAmount =
            typeof plan.coverageAmount === 'string'
              ? parseInt(plan.coverageAmount)
              : plan.coverageAmount;
        } else if (
          fullPlan.category === 'Critical Illness' &&
          plan.priceId &&
          fullPlan.prices
        ) {
          const price = fullPlan.prices.find((p) => p.id === plan.priceId);
          if (price?.data) {
            if ('coverageAmount' in price.data) {
              coverageAmount = price.data.coverageAmount;
            } else if ('mb' in price.data) {
              coverageAmount = price.data.mb;
            }
          }
        }

        if (coverageAmount) {
          (transformedData as unknown as Record<string, unknown>)[
            coverageAmountFieldName
          ] = coverageAmount;
        } else if (fullPlan.category === 'Critical Illness') {
          (transformedData as unknown as Record<string, unknown>)[
            coverageAmountFieldName
          ] = 10000;
        }

        // Restore dependent benefit for Critical Illness
        if (fullPlan.category === 'Critical Illness' && plan.priceId && fullPlan.prices) {
          const ciPrice = fullPlan.prices.find((p) => p.id === plan.priceId);
          const dependentBenefitFieldName = coverageAmountFieldName.replace('CoverageAmount', 'DependentBenefit');
          if (ciPrice?.data && 'db' in ciPrice.data && ciPrice.data.db > 0) {
            (transformedData as unknown as Record<string, unknown>)[
              dependentBenefitFieldName
            ] = ciPrice.data.db;
          } else {
            (transformedData as unknown as Record<string, unknown>)[
              dependentBenefitFieldName
            ] = 5000;
          }
        }

        // Set coverage tags for the plan
        if (plan.applicants && plan.applicants.length > 0) {
          const tags = plan.applicants
            .map((appIndex) =>
              getCoverageTagForApplicantIndex(application.applicants, appIndex)
            )
            .filter((tag): tag is string => !!tag);

          if (fullPlan.category === 'Major Medical') {
            transformedData.medicalTags = tags;
          } else {
            if (!transformedData.planTags) {
              transformedData.planTags = {};
            }
            const planTags = transformedData.planTags as Record<
              string,
              string[]
            >;
            planTags[fullPlan.uid] = tags;
          }
        }
      }
    });
  }

  // 3. Transform Address and SSN
  if (application.address && typeof application.address === 'object') {
    transformedData.address = {
      address1: application.address.address1,
      address2: application.address.address2,
      city: application.address.city,
      state: application.address.state,
      zipcode: application.address.zipcode,
      country: application.address.country,
    };
  }

  // Backwards compat: if root-level SSN exists and no per-applicant SSN, assign to primary
  if (application.ssn && transformedData.applicants) {
    const primaryIdx = transformedData.applicants.findIndex(a => a.applicant === 'primary');
    if (primaryIdx >= 0 && !transformedData.applicants[primaryIdx].ssn) {
      transformedData.applicants[primaryIdx].ssn = application.ssn;
    }
  }

  // 4. Transform PHQ
  if (application.phq) {
    // Handle both old format (IssueDetail[]) and new format (with details)
    const issuesData = application.phq.issues;
    const surgeriesConditions: { value: string; details?: string }[] = [];
    const medDetailsConditions: { value: string; details?: string }[] = [];

    // Process issues - handle both formats
    if (Array.isArray(issuesData)) {
      issuesData.forEach(
        (
          issue:
            | IssueDetail
            | {
                key: string;
                details?: Array<{ key: string; description?: string }>;
              }
        ) => {
          // Check if this is the new format with details
          if (
            'details' in issue &&
            issue.details &&
            Array.isArray(issue.details)
          ) {
            // New format: { key: string, details: Array }
            surgeriesConditions.push({ value: issue.key });

            issue.details.forEach(
              (detail: { key: string; description?: string }) => {
                if (detail.key) {
                  const detailValue = `${issue.key}-${detail.key}`;
                  medDetailsConditions.push({
                    value: detailValue,
                    details: detail.description || '',
                  });
                }
              }
            );
          } else {
            surgeriesConditions.push({ value: (issue as IssueDetail).key });
          }
        }
      );
    }

    transformedData.healthQuestions = {
      initialQuestions: {
        treatment: application.phq.treatment,
        invalid: application.phq.invalid,
        pregnancy: application.phq.pregnancy,
      },
      takingMedicationsNow: {
        value:
          application.phq.medications && application.phq.medications.length > 0,
        medications:
          application.phq.medications &&
          Array.isArray(application.phq.medications)
            ? application.phq.medications.map((med) => {
                // Convert applicant number to string format for dropdown
                let applicantString = '0'; // default to primary
                if (typeof med.applicant === 'number') {
                  applicantString = String(med.applicant);
                } else if (typeof med.applicant === 'string') {
                  if (med.applicant === 'primary') {
                    applicantString = '0';
                  } else if (med.applicant === 'spouse') {
                    applicantString = '1';
                  } else if (med.applicant === 'dependent') {
                    applicantString = '2';
                  } else {
                    // Already a numeric string index ("0", "1", "2", "3", ...)
                    applicantString = med.applicant;
                  }
                }

                // Handle both new and old medication formats for backward compatibility
                if ('id' in med) {
                  // New format
                  // Convert null from API to __OTHER__ for form (null from API means "Other dosage" was selected)
                  const dosageId = med.dosageId === null ? '__OTHER__' : med.dosageId;
                  
                  return {
                    id: med.id,
                    name: (med as any).name,
                    dosageId, // __OTHER__ means "Other dosage" was selected
                    dosageValue: (med as any).dosageValue || '',
                    dosageDescription: med.dosageDescription || '',
                    frequency: med.frequency,
                    frequencyDescription: med.frequencyDescription || '',
                    applicant: applicantString,
                    description: med.description,
                  };
                } else {
                  // Old format - convert to new format (for backward compatibility)
                  // Note: This is a fallback for old data, id should be generated or mapped
                  return {
                    id: (med as any).rxcui || (med as any).name || '', // Fallback to rxcui or name as id
                    name: (med as any).name,
                    dosageId: undefined,
                    dosageDescription: (med as any).dosage || '',
                    frequency: med.frequency,
                    frequencyDescription: med.frequencyDescription || '',
                    applicant: applicantString,
                    description: med.description,
                  };
                }
              })
            : [],
      },
      surgeriesDiabetesHeartMental: {
        conditions: [
          ...surgeriesConditions,
          ...medDetailsConditions.map((c) => ({ value: c.value })),
        ],
        details: {
          ...medDetailsConditions.reduce<Record<string, string>>(
            (acc, c) => {
              if (c.details) acc[c.value] = c.details;
              return acc;
            },
            {}
          ),
        },
      },
      medDetails: {
        medications: [
          ...medDetailsConditions,
          ...(application.phq.medications
            ? application.phq.medications.map((med: Medication) => ({
                value: 'id' in med ? med.id : (med as any).name || '',
                details: med.description || '',
              }))
            : []),
        ],
        details: {
          ...(application.phq.medications
            ? application.phq.medications.reduce(
                (acc: Record<string, string>, med: Medication) => {
                  if (med.description) {
                    const key = 'id' in med ? med.id : (med as any).name || '';
                    acc[key] = med.description;
                  }
                  return acc;
                },
                {} as Record<string, string>
              )
            : {}),
          ...medDetailsConditions.reduce(
            (acc: Record<string, string>, condition) => {
              if (condition.details) {
                acc[condition.value] = condition.details;
              }
              return acc;
            },
            {} as Record<string, string>
          ),
        },
      },
      otherHealthConditions: {
        conditions: application.phq.conditions
          ? application.phq.conditions.map((condition: Condition) => ({
              value: condition.key,
            }))
          : [],
        details: application.phq.conditions
          ? application.phq.conditions.reduce(
              (acc: Record<string, string>, condition: Condition) => {
                if (condition.description) {
                  acc[condition.key] = condition.description;
                }
                return acc;
              },
              {} as Record<string, string>
            )
          : {},
      },
      coverageStartDate: {
        effectiveDate: validateEffectiveDate(application.phq.effectiveDate),
      },
      privacyDisclosure: {
        disclaimer: application.phq.disclaimer,
        signature: application.phq.signature,
        accepted: !!application.phq.signature,
      },
      // NOTE: other fields would need mapping if required
    } as OnboardingSchema['healthQuestions'];
  }

  return transformedData;
}
